import { useEffect, useState } from 'react';
import supabase from '../lib/supabase';

/**
 * Computes whether the active club is ready to take bookings.
 *
 * Four blocking gates:
 *   1. At least one active club_venues row.
 *   2. Stripe Connect onboarded — either at the brand level
 *      (clubs.stripe_onboarding_completed_at) or on any of the venues
 *      (club_venues.stripe_onboarding_completed_at). Customers can
 *      pay either way thanks to venue_stripe_account() resolver.
 *   3. At least one active court somewhere.
 *   4. At least one hours row (venue_hours OR legacy club_hours).
 *
 * Plus a non-blocking "pending activation" hint when everything else
 * is in place but clubs.is_active is still false (admin needs to
 * flip the switch).
 *
 * Wire: a single `club_readiness(p_club_id)` RPC computes all six
 * booleans server-side. Replaces the old five-query fan-out that
 * also serialised a venue-id lookup. See migration
 * 20260426000000_dashboard_stats_and_readiness_rpcs.
 */

export type ReadinessItemKind =
  | 'no_venue'
  | 'no_stripe'
  | 'no_courts'
  | 'no_hours'
  | 'pending_activation';

export type ReadinessItem = {
  kind: ReadinessItemKind;
  label: string;
  description: string;
  // Path to the partner page where the user can fix it.
  href: string;
};

export type Readiness = {
  loading: boolean;
  // True when nothing blocks bookings — covers both "ready and live"
  // and "ready but admin hasn't activated yet".
  ready: boolean;
  // True when clubs.is_active = true.
  isActive: boolean;
  missing: ReadinessItem[];
  // Surface read failures so the banner can render an inline error
  // rather than sitting silently in `loading`.
  error: string | null;
};

type RpcShape = {
  isActive: boolean;
  hasVenue: boolean;
  hasCourt: boolean;
  hasVenueHours: boolean;
  hasClubHours: boolean;
  stripeOnboarded: boolean;
};

const NONE: Readiness = { loading: true, ready: false, isActive: false, missing: [], error: null };

export function useClubReadiness(clubId: string | null): Readiness {
  const [state, setState] = useState<Readiness>(NONE);

  useEffect(() => {
    if (!clubId) { setState({ ...NONE, loading: false }); return; }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));

    (async () => {
      try {
        // club_readiness isn't in the generated rpc-name union;
        // skip the type narrowing the same way we do for club_venues.
        const { data, error } = await (supabase.rpc as any)('club_readiness', {
          p_club_id: clubId,
        });
        if (cancelled) return;
        if (error) throw error;

        const r = (data ?? {}) as RpcShape;
        const missing: ReadinessItem[] = [];
        if (!r.hasVenue) {
          missing.push({
            kind: 'no_venue',
            label: 'Add a venue',
            description: 'Customers need a physical address to find you.',
            href: '/club/venues',
          });
        }
        if (!r.stripeOnboarded) {
          missing.push({
            kind: 'no_stripe',
            label: 'Connect Stripe',
            description: 'Required to receive payouts. Onboarding takes ~5 minutes.',
            href: '/club',
          });
        }
        if (!r.hasCourt) {
          missing.push({
            kind: 'no_courts',
            label: 'Add at least one court',
            description: 'Bookings need at least one bookable court.',
            href: '/club/courts',
          });
        }
        if (!r.hasVenueHours && !r.hasClubHours) {
          missing.push({
            kind: 'no_hours',
            label: 'Set opening hours',
            description: "Without hours we can't validate booking times.",
            href: '/club/schedule',
          });
        }
        // Setup is done but admin hasn't activated yet.
        if (missing.length === 0 && !r.isActive) {
          missing.push({
            kind: 'pending_activation',
            label: 'Pending platform review',
            description: 'AceRent ops will activate your club within 1 business day.',
            href: '/club',
          });
        }

        const blocking = missing.filter((m) => m.kind !== 'pending_activation');
        setState({
          loading: false,
          ready: blocking.length === 0,
          isActive: !!r.isActive,
          missing,
          error: null,
        });
      } catch (err: any) {
        if (cancelled) return;
        setState({
          loading: false,
          ready: false,
          isActive: false,
          missing: [],
          error: err?.message ?? 'Failed to check club readiness.',
        });
      }
    })();
    return () => { cancelled = true; };
  }, [clubId]);

  return state;
}
